import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    pageUrl: string;
    imageUrl: string;
    imageAltText?: string;
    pageId: string;
    titleText: string;
    extendedStyles?: NavigationGridItemStyles;
}

type Props = OwnProps;

export interface NavigationGridItemStyles {
    containerClickable?: ClickablePresentationProps;
    container?: GridContainerProps;
    childPageItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    pageImage?: LazyImageProps;
    pageImageItem?: GridItemProps;
    pageImageWrapper?: InjectableCss;
    pageNameItem?: TypographyProps;
    pageNameLinkItem?: GridItemProps;
    pageNameLinkWrapper?: InjectableCss;
}

export const navigationGridItemStyles: NavigationGridItemStyles = {
    containerClickable: {
        css: css`
            height: 100%;
            width: 100%;
        `,
    },
    container: {
        gap: 20,
    },
    childPageItem: {
        css: css`
            padding-bottom: 30px;
        `,
    },
    innerContainer: {
        gap: 0,
        css: css`
            &:hover {
                box-shadow: 0 5px 5px rgba(0, 0, 0, 0.12), 0 0 5px rgba(0, 0, 0, 0.1);
            }

            height: 100%;
            border: 1px solid ${getColor("common.border")};
        `,
    },
    pageImageWrapper: {
        css: css`
            height: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        min-height 75px;
                    `,
                    css`
                        min-height 75px;
                    `,
                    css`
                        min-height 125px;
                    `,
                    css`
                        min-height 175px;
                    `,
                    css`
                        min-height 175px;
                    `,
                ]);
            }}
        `,
    },
    pageImageItem: {
        width: 12,
        css: css`
            justify-content: center;
            width: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        min-height 75px;
                        padding: 3px;
                    `,
                    css`
                        min-height 75px;
                        padding: 3px;
                    `,
                    css`
                        min-height 125px;
                        padding: 8px;
                    `,
                    css`
                        min-height 175px;
                        padding: 8px;
                    `,
                    css`
                        min-height 175px;
                        padding: 8px;
                    `,
                ]);
            }}
        `,
    },
    pageImage: {
        css: css`
            width: 100%;
            img {
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            padding-left: 0;
                            padding-right: 0;
                            max-height: 100px;
                        `,
                        css`
                            padding-left: 0;
                            padding-right: 0;
                            max-height: 100px;
                        `,
                        css`
                            padding-left: 10px;
                            padding-right: 10px;
                            max-height: 150px;
                        `,
                        css`
                            padding-left: 20px;
                            padding-right: 20px;
                            max-height: 175px;
                        `,
                        css`
                            padding-left: 20px;
                            padding-right: 20px;
                            max-height: 175px;
                        `,
                    ]);
                }}
            }
        `,
    },
    pageNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    pageNameItem: {
        weight: "bold",
        css: css`
            width: 100%;
            text-align: center;
            word-wrap: break-word;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 11px;
                    `,
                    css`
                        font-size: 11px;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    },
    pageNameLinkWrapper: {
        css: css`
            width: 100%;
            padding-left: 5px;
            padding-right: 5px;
        `,
    },
};

const NavigationGridItem: FC<Props> = ({ pageUrl, pageId, imageUrl, imageAltText, titleText, extendedStyles }) => {
    //  must have a pageUrl and either an image or text
    if (!pageUrl || (!imageUrl && !titleText)) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(navigationGridItemStyles, extendedStyles));

    const altText = imageAltText && imageAltText.length > 0 ? imageAltText : titleText;

    return (
        <GridItem key={pageId} {...styles.childPageItem}>
            <Clickable href={pageUrl} {...styles.containerClickable}>
                <GridContainer {...styles.innerContainer} data-test-selector="CmsChildPageLinks_container">
                    <GridItem {...styles.pageImageItem}>
                        <StyledWrapper {...styles.pageImageWrapper}>
                            {imageUrl.length > 0 && (
                                <LazyImage src={imageUrl} altText={altText} {...styles.pageImage} />
                            )}
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.pageNameLinkItem}>
                        {titleText.length > 0 && (
                            <StyledWrapper className="child-wrapper" {...styles.pageNameLinkWrapper}>
                                <Typography {...styles.pageNameItem}>{titleText}</Typography>
                            </StyledWrapper>
                        )}
                    </GridItem>
                </GridContainer>
            </Clickable>
        </GridItem>
    );
};

export default NavigationGridItem;
